/**
 * User Service - Manages Firestore user operations
 * Handles user CRUD, company associations, and preferences
 */

import { db, functions } from '@/js/firebase';
import { httpsCallable } from 'firebase/functions';
import {
  doc,
  getDoc,
  setDoc,
  updateDoc,
  collection,
  query,
  where,
  getDocs,
  serverTimestamp,
  arrayUnion,
  arrayRemove
} from 'firebase/firestore';
import { logger } from './loggerService';
import { companyService } from './companyService';

class UserService {
  constructor() {
    this.collection = 'users';
  }

  /**
   * Get user document from Firestore
   * @param {string} uid - Firebase Auth UID
   * @returns {Promise<Object|null>} User document or null if not found
   */
  async getUser(uid) {
    try {
      logger.info('[UserService] Fetching user', { uid });
      const userDoc = await getDoc(doc(db, this.collection, uid));
      
      if (userDoc.exists()) {
        return { id: userDoc.id, ...userDoc.data() };
      }
      
      logger.warn('[UserService] User not found in Firestore', { uid });
      return null;
    } catch (error) {
      logger.error('[UserService] Error fetching user:', error);
      throw error;
    }
  }

  /**
   * Create a new user document
   * @param {string} uid - Firebase Auth UID
   * @param {Object} userData - User data to store
   * @returns {Promise<Object>} Created user document
   */
  async createUser(uid, userData) {
    try {
      logger.info('[UserService] Creating user', { uid, email: userData.email });
      
      const now = serverTimestamp();
      const userDocument = {
        uid,
        profile: {
          firstName: userData.firstName || '',
          lastName: userData.lastName || '',
          email: userData.email || '',
          avatar: userData.avatar || ''
        },
        companies: userData.companies || [],
        systemRole: (userData.systemRole === 'super' ? 'admin' : userData.systemRole) || 'user',
        metadata: {
          createdAt: now,
          updatedAt: now,
          lastLoginAt: null,
          lastActivityAt: null,
          createdBy: userData.createdBy || 'system'
        }
      };

      // Add optional fields only if they exist and are not undefined
      if (userData.defaultCompanyId !== undefined) {
        userDocument.defaultCompanyId = userData.defaultCompanyId;
      }
      if (userData.invitedBy !== undefined) {
        userDocument.invitedBy = userData.invitedBy;
      }

      await setDoc(doc(db, this.collection, uid), userDocument);
      logger.info('[UserService] User created successfully', { uid });
      
      return { id: uid, ...userDocument };
    } catch (error) {
      logger.error('[UserService] Error creating user:', error);
      throw error;
    }
  }

  /**
   * Update user profile information
   * @param {string} uid - Firebase Auth UID
   * @param {Object} profileData - Profile data to update
   * @returns {Promise<void>}
   */
  async updateProfile(uid, profileData) {
    try {
      logger.info('[UserService] Updating user profile', { uid });

      const updates = {
        'profile': { ...profileData },
        'metadata.updatedAt': serverTimestamp()
      };

      await updateDoc(doc(db, this.collection, uid), updates);
      logger.info('[UserService] Profile updated successfully', { uid });
    } catch (error) {
      logger.error('[UserService] Error updating profile:', error);
      throw error;
    }
  }

  /**
   * Update user document with arbitrary fields
   * Used for admin operations like fixing role mismatches
   * @param {string} uid - Firebase Auth UID
   * @param {Object} updates - Fields to update
   * @returns {Promise<void>}
   */
  async updateUserDocument(uid, updates) {
    try {
      logger.info('[UserService] Updating user document', { uid, fields: Object.keys(updates) });

      const finalUpdates = {
        ...updates,
        'metadata.updatedAt': serverTimestamp()
      };

      await updateDoc(doc(db, this.collection, uid), finalUpdates);
      logger.info('[UserService] User document updated successfully', { uid });
    } catch (error) {
      logger.error('[UserService] Error updating user document:', error);
      throw error;
    }
  }

  /**
   * Update user preferences
   * @param {string} uid - Firebase Auth UID
   * @param {Object} preferences - Preferences to update
   * @returns {Promise<void>}
   */
  async updatePreferences(uid, preferences) {
    try {
      logger.info('[UserService] Updating user preferences', { uid });

      const updates = {
        'preferences': { ...preferences },
        'metadata.updatedAt': serverTimestamp()
      };

      await updateDoc(doc(db, this.collection, uid), updates);
      logger.info('[UserService] Preferences updated successfully', { uid });
    } catch (error) {
      logger.error('[UserService] Error updating preferences:', error);
      throw error;
    }
  }

  /**
   * Update user permissions
   * Uses setDoc with merge to handle legacy users who may not have a Firestore document
   * @param {string} uid - Firebase Auth UID
   * @param {string[]} permissions - Array of permission keys
   * @param {string|null} groupId - Optional permission group ID that was applied
   * @param {string} systemRole - System role to ensure is set (defaults to 'admin')
   * @returns {Promise<void>}
   */
  async updatePermissions(uid, permissions, groupId = null, systemRole = 'admin') {
    try {
      logger.info('[UserService] Updating user permissions', { uid, permissions, groupId, systemRole });

      const updates = {
        permissions: permissions || [],
        permissionGroupId: groupId,
        systemRole: systemRole,
        'metadata.updatedAt': serverTimestamp()
      };

      // Use setDoc with merge to create doc if it doesn't exist (legacy users)
      await setDoc(doc(db, this.collection, uid), updates, { merge: true });
      logger.info('[UserService] Permissions updated successfully', { uid });
    } catch (error) {
      logger.error('[UserService] Error updating permissions:', error);
      throw error;
    }
  }

  /**
   * Update user system role
   * @param {string} uid - Firebase Auth UID
   * @param {string} role - System role ('admin', 'super', or 'user')
   * @returns {Promise<void>}
   */
  async updateSystemRole(uid, role) {
    try {
      const validRoles = ['admin', 'super', 'user'];
      if (!validRoles.includes(role)) {
        throw new Error(`Invalid system role: ${role}. Must be one of: ${validRoles.join(', ')}`);
      }

      logger.info('[UserService] Updating system role', { uid, role });

      const updates = {
        systemRole: role,
        'metadata.updatedAt': serverTimestamp()
      };

      // If changing to super, clear permissions (they have all implicitly)
      if (role === 'super') {
        updates.permissions = [];
        updates.permissionGroupId = null;
      }

      await updateDoc(doc(db, this.collection, uid), updates);
      logger.info('[UserService] System role updated successfully', { uid, role });
    } catch (error) {
      logger.error('[UserService] Error updating system role:', error);
      throw error;
    }
  }

  /**
   * Add a company association to a user
   * @param {string} uid - Firebase Auth UID
   * @param {Object} company - Company object to add
   * @returns {Promise<void>}
   */
  async addCompany(uid, company) {
    try {
      logger.info('[UserService] Adding company to user', { uid, companyId: company.companyId });
      
      // Use a regular Date object instead of serverTimestamp() for arrayUnion
      const companyData = {
        companyId: company.companyId,
        companyName: company.companyName || '',
        role: company.role || 'user',
        joinedAt: new Date(),
        isPrimary: company.isPrimary || false,
        status: 'active'
      };
      
      const updates = {
        companies: arrayUnion(companyData),
        'metadata.updatedAt': serverTimestamp()
      };
      
      // If this is the first company or marked as primary, set as default
      const user = await this.getUser(uid);
      if (!user?.preferences?.defaultCompanyId || company.isPrimary) {
        updates['preferences.defaultCompanyId'] = company.companyId;
      }

      await updateDoc(doc(db, this.collection, uid), updates);
      logger.info('[UserService] Company added successfully', { uid, companyId: company.companyId });

      // Log user addition to company activity
      const userName = user?.profile?.firstName && user?.profile?.lastName
        ? `${user.profile.firstName} ${user.profile.lastName}`
        : user?.profile?.email || 'Unknown User';

      await companyService.logActivity(
        company.companyId,
        'user_added',
        `User: ${userName}`,
        null,
        null,
        user?.profile?.email || null
      );
    } catch (error) {
      logger.error('[UserService] Error adding company:', error);
      throw error;
    }
  }

  /**
   * Remove a company association from a user
   * @param {string} uid - Firebase Auth UID
   * @param {string} companyId - Company ID to remove
   * @returns {Promise<void>}
   */
  async removeCompany(uid, companyId) {
    try {
      logger.info('[UserService] Removing company from user', { uid, companyId });
      
      const user = await this.getUser(uid);
      if (!user) throw new Error('User not found');
      
      const companyToRemove = user.companies.find(c => c.companyId === companyId);
      if (!companyToRemove) throw new Error('Company not found for user');
      
      const updates = {
        companies: arrayRemove(companyToRemove),
        'metadata.updatedAt': serverTimestamp()
      };
      
      // If removing the default company, set a new default
      if (user.preferences?.defaultCompanyId === companyId) {
        const remainingCompanies = user.companies.filter(c => c.companyId !== companyId);
        updates['preferences.defaultCompanyId'] = remainingCompanies[0]?.companyId || '';
      }
      
      await updateDoc(doc(db, this.collection, uid), updates);
      logger.info('[UserService] Company removed successfully', { uid, companyId });
    } catch (error) {
      logger.error('[UserService] Error removing company:', error);
      throw error;
    }
  }

  /**
   * Set the active/default company for a user
   * @param {string} uid - Firebase Auth UID
   * @param {string} companyId - Company ID to set as default
   * @returns {Promise<void>}
   */
  async setActiveCompany(uid, companyId) {
    try {
      logger.info('[UserService] Setting active company', { uid, companyId });
      
      const user = await this.getUser(uid);
      if (!user) throw new Error('User not found');
      
      // Verify user has access to this company
      const hasAccess = user.companies.some(c => c.companyId === companyId);
      if (!hasAccess) throw new Error('User does not have access to this company');
      
      const updates = {
        'preferences.defaultCompanyId': companyId,
        'metadata.updatedAt': serverTimestamp()
      };
      
      await updateDoc(doc(db, this.collection, uid), updates);
      logger.info('[UserService] Active company set successfully', { uid, companyId });
    } catch (error) {
      logger.error('[UserService] Error setting active company:', error);
      throw error;
    }
  }

  /**
   * Update last login timestamp
   * @param {string} uid - Firebase Auth UID
   * @returns {Promise<void>}
   */
  async updateLastLogin(uid) {
    try {
      const updates = {
        'metadata.lastLoginAt': serverTimestamp(),
        'metadata.loginCount': (await this.getUser(uid))?.metadata?.loginCount + 1 || 1
      };
      
      await updateDoc(doc(db, this.collection, uid), updates);
      logger.info('[UserService] Last login updated', { uid });
    } catch (error) {
      logger.error('[UserService] Error updating last login:', error);
      // Don't throw - this is non-critical
    }
  }

  /**
   * Update last activity timestamp
   * @param {string} uid - Firebase Auth UID
   * @returns {Promise<void>}
   */
  async updateLastActivity(uid) {
    try {
      const updates = {
        'metadata.lastActivityAt': serverTimestamp()
      };
      
      await updateDoc(doc(db, this.collection, uid), updates);
    } catch (error) {
      logger.error('[UserService] Error updating last activity:', error);
      // Don't throw - this is non-critical
    }
  }

  /**
   * Check if a user exists by email
   * @param {string} email - User email
   * @returns {Promise<Object|null>} User document or null
   */
  async getUserByEmail(email) {
    try {
      logger.info('[UserService] Searching for user by email', { email });
      
      const q = query(
        collection(db, this.collection),
        where('profile.email', '==', email)
      );
      
      const snapshot = await getDocs(q);
      
      if (!snapshot.empty) {
        const userDoc = snapshot.docs[0];
        return { id: userDoc.id, ...userDoc.data() };
      }
      
      return null;
    } catch (error) {
      logger.error('[UserService] Error searching user by email:', error);
      throw error;
    }
  }

  /**
   * Get all admin and super admin users
   * @returns {Promise<Array>} Array of admin/super users
   */
  async getAdminUsers() {
    try {
      logger.info('[UserService] Fetching admin users');

      // Query for users where systemRole is 'admin' or 'super'
      const q = query(
        collection(db, this.collection),
        where('systemRole', 'in', ['admin', 'super'])
      );

      const snapshot = await getDocs(q);

      const users = [];
      snapshot.docs.forEach(doc => {
        const userData = doc.data();
        users.push({
          id: doc.id,
          uid: doc.id,
          email: userData.profile?.email || '',
          name: `${userData.profile?.firstName || ''} ${userData.profile?.lastName || ''}`.trim(),
          avatar: userData.profile?.avatar || '',
          systemRole: userData.systemRole,
          type: userData.systemRole, // Add type field for compatibility
          ...userData
        });
      });

      logger.info('[UserService] Found admin users', { count: users.length });
      return users;
    } catch (error) {
      logger.error('[UserService] Error fetching admin users:', error);
      throw error;
    }
  }

  /**
   * Get all users with full profile data
   */
  async getAllUsersWithProfiles() {
    try {
      logger.info('[UserService] Fetching all users with profiles');

      // Query all users
      const snapshot = await getDocs(collection(db, this.collection));

      const users = [];
      snapshot.docs.forEach(doc => {
        const userData = doc.data();
        users.push({
          id: doc.id,
          uid: doc.id,
          email: userData.profile?.email || '',
          name: `${userData.profile?.firstName || ''} ${userData.profile?.lastName || ''}`.trim(),
          avatar: userData.profile?.avatar || '',
          systemRole: userData.systemRole,
          type: userData.systemRole,
          profile: userData.profile,
          ...userData
        });
      });

      logger.info('[UserService] All users fetched successfully', { count: users.length });
      return users;
    } catch (error) {
      logger.error('[UserService] Failed to fetch all users:', error);
      throw error;
    }
  }

  /**
   * Get the company's own people.
   *
   * Agency staff hold membership of every company they manage, so they would
   * otherwise appear in each client's user list. They are not that company's
   * people — they belong to the agency — so they are excluded by default.
   * Staff of the agency's OWN company are native to it and always included.
   *
   * @param {string} companyId - Company ID
   * @param {{ includeAgencyStaff?: boolean }} [options]
   * @returns {Promise<Array>} Array of users
   */
  async getUsersByCompany(companyId, { includeAgencyStaff = false } = {}) {
    try {
      logger.info('[UserService] Fetching users for company', { companyId });

      // Fetch all users and filter client-side since Firestore doesn't support
      // querying nested array objects easily
      const snapshot = await getDocs(collection(db, this.collection));

      const users = [];
      snapshot.docs.forEach(doc => {
        const userData = doc.data();
        const companies = userData.companies || [];

        // Check if user belongs to this company
        const belongsToCompany = companies.some(c => {
          if (typeof c === 'string') return c === companyId;
          if (typeof c === 'object') return c.companyId === companyId;
          return false;
        });

        // Agency staff managing this company are not members of it.
        const isVisitingAgencyStaff =
          !!userData.agencyId && userData.agencyId !== companyId;
        if (belongsToCompany && (includeAgencyStaff || !isVisitingAgencyStaff)) {
          users.push({
            id: doc.id,
            uid: doc.id,
            email: userData.profile?.email || userData.email || '',
            name: `${userData.profile?.firstName || ''} ${userData.profile?.lastName || ''}`.trim() || userData.name || '',
            avatar: userData.profile?.avatar || '',
            systemRole: userData.systemRole,
            type: userData.systemRole,
            profile: userData.profile,
            companyId: companyId,
            ...userData
          });
        }
      });

      logger.info('[UserService] Found users for company', { companyId, count: users.length });
      return users;
    } catch (error) {
      logger.error('[UserService] Error fetching company users:', error);
      throw error;
    }
  }

  /**
   * Get all users for a company (alias for getUsersByCompany)
   * @param {string} companyId - Company ID
   * @param {{ includeAgencyStaff?: boolean }} [options]
   * @returns {Promise<Array>} Array of users
   */
  async getCompanyUsers(companyId, options) {
    return this.getUsersByCompany(companyId, options);
  }


  /**
   * Remove a user from a company
   * If this is the user's only company, deletes both the Firestore document and Auth user
   * If the user has other companies, just removes them from this company
   * @param {string} uid - User ID
   * @param {string} companyId - Company ID
   * @returns {Promise<Object>} Object with deletedUser boolean indicating if user was fully deleted
   */
  async removeUserFromCompany(uid, companyId, { deleteIfOrphaned = true } = {}) {
    try {
      logger.info('[UserService] Removing user from company', { uid, companyId });

      const user = await this.getUser(uid);
      if (!user) throw new Error('User not found');

      // Server-enforced: firestore.rules no longer let client users write companies[]
      const manageMember = httpsCallable(functions, 'manageCompanyMember');
      const { data } = await manageMember({ userId: uid, companyId, action: 'remove' });

      const userName = user?.profile?.firstName && user?.profile?.lastName
        ? `${user.profile.firstName} ${user.profile.lastName}`
        : user?.profile?.email || 'Unknown User';

      await companyService.logActivity(
        companyId,
        'user_removed',
        `User: ${userName}`,
        null,
        null,
        user?.profile?.email || null
      );

      if (data.orphaned && deleteIfOrphaned) {
        logger.info('[UserService] User has no remaining companies, deleting user completely', { uid, companyId });
        await this.deleteUser(uid);
        return { deletedUser: true };
      }

      logger.info('[UserService] User removed from company successfully', { uid, companyId });
      return { deletedUser: false };
    } catch (error) {
      logger.error('[UserService] Error removing user from company:', error);
      throw error;
    }
  }

  /**
   * Delete a user completely (both Firestore document and Auth user)
   * @param {string} uid - User ID
   * @returns {Promise<void>}
   */
  async deleteUser(uid) {
    try {
      logger.info('[UserService] Deleting user completely', { uid });

      // Import Firebase functions
      const { getFunctions, httpsCallable } = await import('firebase/functions');
      const functions = getFunctions();

      // Get user data before deletion for logging
      const user = await this.getUser(uid);

      // Delete Firestore document first
      const { deleteDoc } = await import('firebase/firestore');
      await deleteDoc(doc(db, this.collection, uid));
      logger.info('[UserService] Firestore document deleted', { uid });

      // Delete Firebase Auth user
      const deleteUserFunction = httpsCallable(functions, 'deleteUser');
      await deleteUserFunction({ user: { uid } });
      logger.info('[UserService] Auth user deleted', { uid });

      logger.info('[UserService] User deleted completely', {
        uid,
        email: user?.profile?.email
      });
    } catch (error) {
      logger.error('[UserService] Error deleting user:', error);
      throw error;
    }
  }

  /**
   * Set primary contact for a company
   * @param {string} companyId - Company ID
   * @param {string} userId - User ID to set as primary contact
   * @returns {Promise<void>}
   */
  async setPrimaryContact(companyId, userId) {
    try {
      logger.info('[UserService] Setting primary contact', { companyId, userId });
      
      // Update company document with primary contact
      const companyRef = doc(db, 'companies', companyId);
      await updateDoc(companyRef, {
        primaryContactId: userId,
        'metadata.updatedAt': serverTimestamp()
      });
      
      logger.info('[UserService] Primary contact set successfully', { companyId, userId });
    } catch (error) {
      logger.error('[UserService] Error setting primary contact:', error);
      throw error;
    }
  }

  /**
   * Create user or add to company if user already exists
   * NOTE: This method only manages Firestore documents. For creating new users with authentication,
   * use the createAuthUserAndAddToCompany method instead.
   * @param {string} email - User email
   * @param {Object} userData - User data (firstName, lastName, etc.)
   * @param {string} companyId - Company ID to associate with user
   * @param {string} uid - Optional Firebase Auth UID (required for new users)
   * @returns {Promise<Object>} User document
   */
  async createUserOrAddToCompany(email, userData, companyId, uid = null) {
    try {
      logger.info('[UserService] Creating user or adding to company', { email, companyId });

      // Check if user already exists
      const existingUser = await this.getUserByEmail(email);

      if (existingUser) {
        logger.info('[UserService] User exists, adding company to existing user', {
          userId: existingUser.id,
          email,
          companyId
        });

        // Check if user already belongs to this company
        const alreadyInCompany = existingUser.companies?.some(company => {
          if (typeof company === 'string') return company === companyId;
          if (typeof company === 'object') return company.companyId === companyId;
          return false;
        });

        if (alreadyInCompany) {
          logger.info('[UserService] User already belongs to company', { userId: existingUser.id, companyId });
          return existingUser;
        }

        // Get company details to include name
        const companyDoc = await getDoc(doc(db, 'companies', companyId));
        const companyName = companyDoc.exists() ? companyDoc.data().name : '';

        // Add company to existing user
        const companyData = {
          companyId: companyId,
          companyName: companyName,
          joinedAt: new Date()
        };

        const updates = {
          companies: arrayUnion(companyData),
          'metadata.updatedAt': serverTimestamp()
        };

        await updateDoc(doc(db, this.collection, existingUser.id), updates);

        // Return updated user data
        const updatedUser = await this.getUser(existingUser.id);
        logger.info('[UserService] Company added to existing user successfully', {
          userId: existingUser.id,
          companyId
        });

        // Log user addition to company activity
        const userName = existingUser?.profile?.firstName && existingUser?.profile?.lastName
          ? `${existingUser.profile.firstName} ${existingUser.profile.lastName}`
          : existingUser?.profile?.email || 'Unknown User';

        await companyService.logActivity(
          companyId,
          'user_added',
          `User: ${userName}`,
          null,
          null,
          existingUser?.profile?.email || null
        );

        return updatedUser;
      } else {
        // For new users, require a valid Firebase Auth UID
        if (!uid) {
          throw new Error('Firebase Auth UID is required for creating new users. Use createAuthUserAndAddToCompany instead.');
        }

        logger.info('[UserService] Creating new Firestore document for user', { email, companyId, uid });

        // Get company details to include name
        const companyDoc = await getDoc(doc(db, 'companies', companyId));
        const companyName = companyDoc.exists() ? companyDoc.data().name : '';

        // Create new user with company association
        const newUserData = {
          firstName: userData.firstName || '',
          lastName: userData.lastName || '',
          email: email,
          systemRole: userData.systemRole || 'user',
          companies: [{
            companyId: companyId,
            companyName: companyName,
            joinedAt: new Date()
          }],
          createdBy: userData.createdBy || 'system'
        };

        const newUser = await this.createUser(uid, newUserData);
        logger.info('[UserService] New user Firestore document created with company association', {
          userId: uid,
          email,
          companyId
        });

        // Log user addition to company activity
        const userName = newUserData.firstName && newUserData.lastName
          ? `${newUserData.firstName} ${newUserData.lastName}`
          : email;

        await companyService.logActivity(
          companyId,
          'user_added',
          `User: ${userName}`,
          null,
          null,
          email || null
        );

        return newUser;
      }
    } catch (error) {
      logger.error('[UserService] Error in createUserOrAddToCompany:', error);
      throw error;
    }
  }

  /**
   * Create a new user with Firebase Auth and add to company
   * This method creates both the Firebase Auth user and Firestore document
   * @param {string} email - User email
   * @param {Object} userData - User data (firstName, lastName, password, etc.)
   * @param {string} companyId - Company ID to associate with user
   * @returns {Promise<Object>} User document
   */
  async createAuthUserAndAddToCompany(email, userData, companyId) {
    try {
      logger.info('[UserService] Creating auth user and adding to company', { email, companyId });

      // Import Firebase functions
      const { getFunctions, httpsCallable } = await import('firebase/functions');
      const functions = getFunctions();

      // First check if user already exists
      const existingUser = await this.getUserByEmail(email);
      if (existingUser) {
        logger.info('[UserService] User already exists, adding to company', {
          userId: existingUser.id,
          email,
          companyId
        });
        // Use the existing method to add company
        return this.createUserOrAddToCompany(email, userData, companyId);
      }

      // Create Firebase Auth user
      const addUserFunction = httpsCallable(functions, 'addUser');

      // Generate a random password if not provided
      const password = userData.password || this.generateRandomPassword();

      const authResult = await addUserFunction({
        email: email,
        password: password,
        systemRole: userData.systemRole || 'user',
        // Pass companies array so claims are set correctly on creation
        companies: [{ companyId: companyId }]
      });

      logger.info('[UserService] Firebase Auth user created', {
        uid: authResult.data.uid,
        email
      });

      // Now create the Firestore document with the auth UID
      const userDoc = await this.createUserOrAddToCompany(
        email,
        userData,
        companyId,
        authResult.data.uid
      );

      // Send welcome email if requested
      if (userData.sendWelcomeEmail !== false) {
        try {
          const welcomeEmailFunction = httpsCallable(functions, 'welcomeEmail');
          await welcomeEmailFunction({
            email: email,
            firstName: userData.firstName || '',
            companyId: companyId,
            temporaryPassword: password
          });
          logger.info('[UserService] Welcome email sent', { email });
        } catch (emailError) {
          logger.error('[UserService] Failed to send welcome email:', emailError);
          // Don't throw - user was created successfully
        }
      }

      return userDoc;
    } catch (error) {
      logger.error('[UserService] Error creating auth user:', error);
      throw error;
    }
  }

  /**
   * Generate a random password
   * @returns {string} Random password
   */
  generateRandomPassword() {
    const length = 12;
    const charset = 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!@#$%^&*';
    let password = '';
    for (let i = 0; i < length; i++) {
      password += charset.charAt(Math.floor(Math.random() * charset.length));
    }
    return password;
  }

  /**
   * Update a user's permissionLevel within their companies array entry
   * @param {string} uid - Firebase Auth UID
   * @param {string} companyId - Company ID to update permission for
   * @param {string} permissionLevel - One of 'view-only', 'manager', 'administrator'
   * @returns {Promise<void>}
   */
  async updateCompanyPermission(uid, companyId, permissionLevel) {
    try {
      logger.info('[UserService] Updating company permission', { uid, companyId, permissionLevel });

      // Server-enforced: the callable applies ASSIGNABLE_LEVELS to the caller
      const manageMember = httpsCallable(functions, 'manageCompanyMember');
      await manageMember({ userId: uid, companyId, action: 'setPermission', permissionLevel });

      logger.info('[UserService] Company permission updated successfully', { uid, companyId, permissionLevel });
    } catch (error) {
      logger.error('[UserService] Error updating company permission:', error);
      throw error;
    }
  }

  /**
   * Mark a tour as seen for a user
   * Uses dot-notation to set a single key inside preferences.toursSeen
   * @param {string} uid - Firebase Auth UID
   * @param {string} tourId - Tour identifier (e.g. 'reviewDetail')
   * @returns {Promise<void>}
   */
  async markTourSeen(uid, tourId) {
    try {
      logger.info('[UserService] Marking tour as seen', { uid, tourId });
      await updateDoc(doc(db, this.collection, uid), {
        [`preferences.toursSeen.${tourId}`]: serverTimestamp(),
      });
      logger.info('[UserService] Tour marked as seen', { uid, tourId });
    } catch (error) {
      logger.error('[UserService] Error marking tour as seen:', error);
      throw error;
    }
  }

  /**
   * Create or update user from Auth data
   * Handles migration from custom claims to Firestore
   * @param {Object} authUser - Firebase Auth user object
   * @param {Object} customClaims - Custom claims from Auth
   * @returns {Promise<Object>} User document
   */
  async createOrUpdateFromAuth(authUser, customClaims) {
    try {
      logger.info('[UserService] Creating/updating user from Auth', { 
        uid: authUser.uid, 
        email: authUser.email 
      });
      
      // Check if user exists in Firestore
      let user = await this.getUser(authUser.uid);
      
      if (!user) {
        // Create new user from Auth data
        const nameParts = (authUser.displayName || '').split(' ');
        const firstName = nameParts[0] || '';
        const lastName = nameParts.slice(1).join(' ') || '';
        
        const companies = [];
        if (customClaims?.companyId) {
          companies.push({
            companyId: customClaims.companyId,
            companyName: customClaims.company || '',
            role: 'user',
            joinedAt: serverTimestamp(),
            isPrimary: true,
            status: 'active'
          });
        }
        
        user = await this.createUser(authUser.uid, {
          firstName,
          lastName,
          email: authUser.email,
          phone: authUser.phoneNumber || '',
          avatar: authUser.photoURL || '',
          companies,
          systemRole: (customClaims?.usertype === 'super' ? 'admin' : customClaims?.usertype) || 'user',
          defaultCompanyId: customClaims?.companyId || '',
          createdBy: 'auth_migration'
        });
      }
      
      // Update last login
      await this.updateLastLogin(authUser.uid);

      return user;
    } catch (error) {
      logger.error('[UserService] Error creating/updating from Auth:', error);
      throw error;
    }
  }

  /**
   * Update permissions for all users with a specific permission group
   * Used when a permission group is edited to retroactively apply changes
   * @param {string} groupId - Permission group ID
   * @param {string[]} permissions - New permissions array
   * @returns {Promise<number>} Number of users updated
   */
  async updateUsersWithPermissionGroup(groupId, permissions) {
    try {
      logger.info('[UserService] Updating users with permission group', { groupId, permissionsCount: permissions.length });

      const q = query(
        collection(db, this.collection),
        where('permissionGroupId', '==', groupId)
      );

      const snapshot = await getDocs(q);
      let updatedCount = 0;

      for (const userDoc of snapshot.docs) {
        try {
          await updateDoc(doc(db, this.collection, userDoc.id), {
            permissions: permissions,
            'metadata.updatedAt': serverTimestamp()
          });
          updatedCount++;
        } catch (error) {
          logger.error('[UserService] Failed to update user permissions', { userId: userDoc.id, error: error.message });
        }
      }

      logger.info('[UserService] Updated users with permission group', { groupId, updatedCount });
      return updatedCount;
    } catch (error) {
      logger.error('[UserService] Error updating users with permission group:', error);
      throw error;
    }
  }
}

// Export singleton instance
export const userService = new UserService();